// What the journeys view (Console D, APS-316) derives from the read API: each step's change
// against the previous release, the funnel's worst drop (the view's one accent), the screen the
// paths open on, and a path tree as rows.
import type { Funnel, JourneyBranch, Journeys } from '../api/types'

type Step = Funnel['steps'][number]

/**
 * A step's conversion from the step before, against the same step in the previous release's
 * funnel, in percentage points; undefined when either side is missing.
 */
export function deltaPoints(step: Step | undefined, before: Step | undefined): number | undefined {
  const a = step?.conversion_from_previous
  const b = before?.conversion_from_previous
  if (a === null || a === undefined || b === null || b === undefined) return undefined
  if (step?.screen !== before?.screen) return undefined
  return (a - b) * 100
}

/**
 * The funnel's worst drop, as the index of the step it leads to (1 or more): the largest fall
 * against the previous release, or the lowest conversion when nothing fell or there is no
 * previous release. Undefined for a funnel without a second step that anyone reached.
 */
export function worstDrop(steps: Step[], before: Step[] | undefined): number | undefined {
  let fall: { i: number; d: number } | undefined
  let low: { i: number; c: number } | undefined
  for (let i = 1; i < steps.length; i++) {
    const s = steps[i] as Step
    const d = deltaPoints(s, before?.[i])
    if (d !== undefined && d < 0 && (!fall || d < fall.d)) fall = { i, d }
    const c = s.conversion_from_previous
    if (c !== null && (!low || c < low.c)) low = { i, c }
  }
  return fall?.i ?? low?.i
}

/** The screen the paths open on: the URL's, or the one the worst drop stopped at, or the first. */
export function pathScreen(
  picked: string | undefined,
  steps: Step[] | undefined,
  worst: number | undefined,
  screens: string[],
): string | undefined {
  if (picked) return picked
  const at = worst !== undefined ? steps?.[worst - 1]?.screen : undefined
  return at || screens[0]
}

export interface PathRow {
  key: string
  kind: 'screen' | 'end' | 'other'
  /** The screen; empty for the end and other rows. */
  screen: string
  sessions: number
  /** Its share of the row above it in the tree (the screen's sessions at the root). */
  share: number
  /** 0 for the screens next to the picked one. */
  depth: number
}

/**
 * A path tree as rows, as the mock lists it: each screen with its share of its parent, its own
 * branches under it, then the sessions that ended there and the ones on paths not listed. Rows
 * go `depth` screens deep: the API counts each screen's ended sessions at every depth, but at
 * depth 1 they belong to no row the view shows.
 */
export function pathRows(journeys: Journeys, depth: number): PathRow[] {
  const rows: PathRow[] = []
  const walk = (
    branches: JourneyBranch[],
    ended: number,
    other: number | null,
    total: number,
    level: number,
    key: string,
  ) => {
    const share = (n: number) => (total > 0 ? n / total : 0)
    if (level >= depth) return
    for (const b of branches) {
      const k = `${key}/${b.screen}`
      rows.push({
        key: k,
        kind: 'screen',
        screen: b.screen,
        sessions: b.sessions,
        share: share(b.sessions),
        depth: level,
      })
      walk(b.branches, b.ended, b.other, b.sessions, level + 1, k)
    }
    if (ended > 0)
      rows.push({
        key: `${key}/·end`,
        kind: 'end',
        screen: '',
        sessions: ended,
        share: share(ended),
        depth: level,
      })
    if (other)
      rows.push({
        key: `${key}/·other`,
        kind: 'other',
        screen: '',
        sessions: other,
        share: share(other),
        depth: level,
      })
  }
  walk(
    journeys.branches as unknown as JourneyBranch[],
    journeys.ended,
    journeys.other,
    journeys.sessions,
    0,
    '',
  )
  return rows
}
